import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faPen,
  faTrashCan,
  faXmark,
  faCircleCheck,
  faTriangleExclamation,
  faRotate,
  faAward,
  faFilePdf,
  faImage,
  faUpload,
  faEnvelope,
  faCalendarDays,
  faWandMagicSparkles,
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import { makeCertificate, pickDesign } from '../components/Certificates/certificateMaker';
import './Certificateslegacy.css'; // LEGACY: old global "sp-" classes other old pages may use
import './Certificates.css';

const API = process.env.REACT_APP_API_BASE_URL;
const FILES = process.env.REACT_APP_API_BASE_URL1;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

const pdfUrl = (name) => `${FILES}/assets/Certificates/${name}`;
const imgUrl = (name) => `${FILES}/assets/img/certificates/${name}`;

// today's date on this computer, as YYYY-MM-DD (the old toISOString() gave yesterday late at night)
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const EMPTY = () => ({
  Id: null,
  ModuleName: '',
  ModuleId: '',
  Link: '',
  Date: today(),
  Img: null,
  ImgFile: null,
  LinkFile: null,
  FirstName: '',
  LastName: '',
  SendEmailCongrats: false,
});

const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    return format(parseISO(dateString), 'MMM dd, yyyy');
  } catch {
    return '';
  }
};

const errText = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

const sizeText = (bytes) => (bytes > 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

/* One file picker (PDF or image). A new file is uploaded right away, with a progress bar,
   so you can see it is ready before pressing Save. */
const FilePick = ({ label, icon, name, accept, file, current, upload, onPick, onClear, onRetry }) => {
  const status = file ? upload?.status : null;
  const pct = Math.round((upload?.progress || 0) * 100);
  return (
    <div className="rmact-field">
      <span>{label}</span>
      <label className={`rmact-file ${file || current ? 'has-file' : ''} ${status ? `is-${status}` : ''}`}>
        <input type="file" name={name} accept={accept} onChange={onPick} disabled={status === 'uploading'} />
        <FontAwesomeIcon
          icon={status === 'done' ? faCircleCheck : status === 'error' ? faTriangleExclamation : file || current ? icon : faUpload}
          className="rmact-file-icon"
        />
        <span className="rmact-file-text">
          <strong>{file ? file.name : current || 'Choose a file'}</strong>
          <small>
            {status === 'uploading'
              ? `Uploading… ${pct}% of ${sizeText(file.size)}`
              : status === 'done'
              ? `Uploaded (${sizeText(file.size)}) · ready to save`
              : status === 'error'
              ? upload.error || 'Upload failed'
              : current
              ? 'Current file · tap to replace'
              : accept === '.pdf'
              ? 'PDF'
              : 'JPG or PNG'}
          </small>
          {status && (
            <span className={`rmact-upbar is-${status}`} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={status === 'done' ? 100 : pct} aria-label={`${label} upload`}>
              <i style={{ width: `${status === 'done' ? 100 : pct}%` }} />
            </span>
          )}
        </span>
      </label>
      {status === 'error' && (
        <button type="button" className="rmact-linkbtn" onClick={onRetry}>
          Try the upload again
        </button>
      )}
      {file && status !== 'uploading' && (
        <button type="button" className="rmact-linkbtn" onClick={onClear}>
          {current ? 'Keep the current file instead' : 'Remove this file'}
        </button>
      )}
    </div>
  );
};

const FIELD_OF = { LinkFile: 'Link', ImgFile: 'Img' };

/* Same props as before: { studentId, studentType } (+ studentName for "Create from template") */
// quickAdd = a module id: open the "Add certificate" form for that module straight away, show only
// the form (used from the Certificates page), then onQuickDone(saved) when it closes.
const Certificates = ({ studentId, studentType, studentName = '', quickAdd = null, onQuickDone }) => {
  const quickOpened = useRef(false);
  const quickSaved = useRef(false);
  // certificate templates (one per module): moduleId -> { url, config }
  const [templates, setTemplates] = useState({});
  const [certName, setCertName] = useState(studentName);
  const [making, setMaking] = useState(false);
  const [makeError, setMakeError] = useState('');
  const [certificates, setCertificates] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [reload, setReload] = useState(0);

  const [showModal, setShowModal] = useState(false);
  const [currentCertificate, setCurrentCertificate] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [preview, setPreview] = useState(''); // preview of a newly chosen image
  const [uploads, setUploads] = useState({}); // LinkFile / ImgFile -> { status, progress, name, error }
  const uploadCtl = useRef({});

  const [toDelete, setToDelete] = useState(null); // certificate row
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [toast, setToast] = useState('');

  // ---------- Load (same endpoints) ----------
  const fetchCertificates = useCallback(async () => {
    const res = await axios.get(`${API}/api/admin/mystudents/student-certificates/${studentId}`, auth());
    setCertificates(res.data?.certificates || []);
  }, [studentId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError('');
    (async () => {
      try {
        const modulesResponse = await axios.get(`${API}/api/teacher/Curriculum/modules/${studentType}`, auth());
        if (!cancelled) setModules(Array.isArray(modulesResponse.data) ? modulesResponse.data : []);
        await fetchCertificates();
        // templates are optional: the page works the same without them
        axios
          .get(`${API}/api/admin/certificate-templates`, auth())
          .then((res) => {
            if (cancelled) return;
            // moduleId -> { designs, noCertificate }
            const map = {};
            (res.data?.modules || []).forEach((m) => {
              map[String(m.id)] = { designs: m.designs || {}, noCertificate: !!m.noCertificate };
            });
            setTemplates(map);
          })
          .catch(() => {});
      } catch (err) {
        if (!cancelled) {
          setLoadError(errText(err, 'We couldn’t load the certificates.'));
          setCertificates([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentType, fetchCertificates, reload]);

  useEffect(() => {
    if (!quickAdd || loading || loadError || quickOpened.current) return;
    quickOpened.current = true;
    setCertName(studentName);
    setMakeError('');
    setCurrentCertificate({ ...EMPTY(), ModuleId: String(quickAdd) });
    setFormError('');
    setShowModal(true);
  }, [quickAdd, loading, loadError, studentName]);

  const quickReported = useRef(false);
  useEffect(() => {
    if (!quickAdd || !quickOpened.current || showModal || quickReported.current || !onQuickDone) return;
    quickReported.current = true;
    onQuickDone(quickSaved.current);
  }, [quickAdd, showModal, onQuickDone]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // image preview for a new file
  useEffect(() => {
    if (!currentCertificate.ImgFile) {
      setPreview('');
      return undefined;
    }
    const url = URL.createObjectURL(currentCertificate.ImgFile);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [currentCertificate.ImgFile]);

  // Escape closes the open window
  useEffect(() => {
    if (!showModal && !toDelete) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (toDelete) {
        if (!deleteBusy) setToDelete(null);
      } else if (!saving) setShowModal(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showModal, toDelete, saving, deleteBusy]);

  // ---------- Form ----------
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentCertificate((prev) => ({ ...prev, [name]: value }));
  };

  // ---------- Upload a file as soon as it is chosen ----------
  const startUpload = (key, file) => {
    uploadCtl.current[key]?.abort();
    const ctl = new AbortController();
    uploadCtl.current[key] = ctl;
    setUploads((u) => ({ ...u, [key]: { status: 'uploading', progress: 0 } }));
    const fd = new FormData();
    fd.append(FIELD_OF[key], file);
    axios
      .post(`${API}/api/admin/mystudents/student-certificates/upload-file`, fd, {
        headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' },
        signal: ctl.signal,
        onUploadProgress: (ev) => {
          if (!ev.total) return;
          // keep 99% until the server answers
          setUploads((u) => ({ ...u, [key]: { status: 'uploading', progress: Math.min(0.99, ev.loaded / ev.total) } }));
        },
      })
      .then((res) => {
        if (uploadCtl.current[key] !== ctl) return;
        setUploads((u) => ({ ...u, [key]: { status: 'done', progress: 1, name: res.data?.filename } }));
      })
      .catch((err) => {
        if (ctl.signal.aborted || uploadCtl.current[key] !== ctl) return;
        setUploads((u) => ({ ...u, [key]: { status: 'error', progress: 0, error: err?.response?.data?.message || 'Upload failed. Check the connection and try again.' } }));
      });
  };

  const cancelUploads = () => {
    Object.values(uploadCtl.current).forEach((c) => c?.abort());
    uploadCtl.current = {};
    setUploads({});
  };

  const clearFile = (key) => {
    uploadCtl.current[key]?.abort();
    delete uploadCtl.current[key];
    setUploads((u) => ({ ...u, [key]: undefined }));
    setCurrentCertificate((p) => ({ ...p, [key]: null }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files && files[0]) {
      const file = files[0];
      setCurrentCertificate((prev) => ({ ...prev, [name]: file }));
      startUpload(name, file);
    }
    e.target.value = ''; // lets the same file be chosen again
  };

  const busyUploading = Object.values(uploads).some((u) => u?.status === 'uploading');
  const failedUpload = ['LinkFile', 'ImgFile'].some((k) => currentCertificate[k] && uploads[k]?.status === 'error');

  const handleAddCertificate = () => {
    cancelUploads();
    setCertName(studentName);
    setMakeError('');
    setCurrentCertificate(EMPTY());
    setFormError('');
    setShowModal(true);
  };

  const handleEditCertificate = (certificate) => {
    cancelUploads();
    setCertName(studentName);
    setMakeError('');
    setCurrentCertificate({
      ...certificate,
      Date: (certificate.Date || '').split('T')[0],
      ModuleId: certificate.Id, // same as before: the row's Id is the module id
      ImgFile: null,
      LinkFile: null,
      SendEmailCongrats: false,
    });
    setFormError('');
    setShowModal(true);
  };

  // "Create from template": the module's design + the name + the issue date -> PDF + image,
  // then uploaded like files you chose yourself
  const tplModule = templates[String(currentCertificate.ModuleId || '')] || null;
  const template = pickDesign(tplModule?.designs, studentType); // this student's level first, else the shared design
  const createFromTemplate = async () => {
    if (!template?.config) return;
    if (!certName.trim()) return setMakeError('Write the name to print on the certificate.');
    setMaking(true);
    setMakeError('');
    try {
      const moduleName = modules.find((m) => String(m.Id) === String(currentCertificate.ModuleId))?.ModuleName || 'certificate';
      const out = await makeCertificate({
        moduleId: currentCertificate.ModuleId,
        plan: template.plan,
        config: template.config,
        name: certName.trim(),
        date: currentCertificate.Date,
        fileBase: `${certName.trim()} ${moduleName}`,
      });
      URL.revokeObjectURL(out.previewUrl);
      setCurrentCertificate((p) => ({ ...p, LinkFile: out.pdf, ImgFile: out.jpg }));
      startUpload('LinkFile', out.pdf);
      startUpload('ImgFile', out.jpg);
    } catch (err) {
      setMakeError(err.message || 'Couldn’t create the certificate.');
    } finally {
      setMaking(false);
    }
    return undefined;
  };

  // same endpoints + same FormData fields as before
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busyUploading) return setFormError('Please wait until the files finish uploading.');
    if (failedUpload) return setFormError('A file did not upload. Try the upload again or remove it.');
    setSaving(true);
    setFormError('');
    try {
      const headers = { ...auth().headers, 'Content-Type': 'multipart/form-data' };
      const formData = new FormData();
      formData.append('ModuleId', currentCertificate.ModuleId);
      formData.append('Date', currentCertificate.Date);
      formData.append('FirstName', currentCertificate.FirstName || '');
      formData.append('LastName', currentCertificate.LastName || '');
      formData.append('IdUser', studentId);
      formData.append('SendEmailCongrats', currentCertificate.SendEmailCongrats === true);

      // files were already uploaded: send their saved names
      if (currentCertificate.LinkFile && uploads.LinkFile?.name) formData.append('LinkName', uploads.LinkFile.name);
      else if (currentCertificate.Link) formData.append('Link', currentCertificate.Link);

      if (currentCertificate.ImgFile && uploads.ImgFile?.name) formData.append('ImgName', uploads.ImgFile.name);
      else if (currentCertificate.Img) formData.append('Img', currentCertificate.Img);

      const editing = Boolean(currentCertificate.certificatesId);
      if (editing) {
        await axios.put(
          `${API}/api/admin/mystudents/student-certificates/update/${currentCertificate.certificatesId}`,
          formData,
          { headers }
        );
      } else {
        await axios.post(`${API}/api/admin/mystudents/student-certificates/add`, formData, { headers });
      }
      await fetchCertificates();
      quickSaved.current = true;
      setShowModal(false);
      setToast(
        `${editing ? 'Certificate updated' : 'Certificate added'}${currentCertificate.SendEmailCongrats ? ' · email sent to parents' : ''}`
      );
    } catch (err) {
      setFormError(errText(err, 'Couldn’t save the certificate. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  // ---------- Delete (same endpoint) ----------
  const confirmDelete = async () => {
    setDeleteBusy(true);
    setDeleteError('');
    try {
      await axios.delete(`${API}/api/admin/mystudents/student-certificates/delete/${toDelete.certificatesId}`, auth());
      await fetchCertificates();
      setToDelete(null);
      setToast('Certificate deleted');
    } catch (err) {
      setDeleteError(errText(err, 'Couldn’t delete. Please try again.'));
    } finally {
      setDeleteBusy(false);
    }
  };

  // ---------- States ----------
  if (quickAdd && (loading || loadError)) {
    return (
      <div className="rmact">
        <div className="rmact-overlay" onMouseDown={(e) => e.target === e.currentTarget && onQuickDone && onQuickDone(false)}>
          <div className="rmact-dialog" role="status">
            {loading ? (
              <p>Opening the form…</p>
            ) : (
              <>
                <p className="rmact-error">{loadError}</p>
                <button type="button" className="rmact-btn rmact-btn--ghost" onClick={() => onQuickDone && onQuickDone(false)}>
                  Close
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="rmact" aria-busy="true">
        <div className="rmact-skel rmact-skel--bar" />
        <div className="rmact-grid">
          <div className="rmact-skel" />
          <div className="rmact-skel" />
          <div className="rmact-skel" />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="rmact">
        <div className="rmact-empty">
          <FontAwesomeIcon icon={faTriangleExclamation} className="is-error" />
          <p>{loadError}</p>
          <button type="button" className="rmact-btn" onClick={() => setReload((n) => n + 1)}>
            <FontAwesomeIcon icon={faRotate} /> Try again
          </button>
        </div>
      </div>
    );
  }

  const isEdit = Boolean(currentCertificate.certificatesId);
  // modules that already have a certificate are hidden (except the one being edited)
  const certified = new Set(certificates.map((c) => String(c.Id)));
  const availableModules = modules.filter((m) => !certified.has(String(m.Id)) || String(m.Id) === String(currentCertificate.ModuleId));

  const sorted = [...certificates].sort((a, b) => String(b.Date || '').localeCompare(String(a.Date || '')));

  return (
    <div className="rmact">
      {!quickAdd && (
        <>
      {/* ---------- Head ---------- */}
      <div className="rmact-head">
        <p className="rmact-count">
          <FontAwesomeIcon icon={faAward} />
          <b>{certificates.length}</b> {certificates.length === 1 ? 'certificate' : 'certificates'}
        </p>
        <button type="button" className="rmact-btn" onClick={handleAddCertificate}>
          <FontAwesomeIcon icon={faPlus} /> Add certificate
        </button>
      </div>

      {/* ---------- Cards ---------- */}
      {sorted.length === 0 ? (
        <div className="rmact-empty">
          <FontAwesomeIcon icon={faAward} />
          <p>No certificates yet.</p>
          <span>Add one when the student finishes a module.</span>
        </div>
      ) : (
        <ul className="rmact-grid">
          {sorted.map((cert, i) => (
            <li key={cert.certificatesId ?? `${cert.ModuleName}-${cert.Date}-${i}`} className="rmact-card">
              <div className="rmact-thumb">
                {cert.Img ? (
                  <a href={imgUrl(cert.Img)} target="_blank" rel="noopener noreferrer" aria-label="Open certificate image">
                    <img src={imgUrl(cert.Img)} alt={`${cert.ModuleName} certificate`} loading="lazy" />
                  </a>
                ) : (
                  <span className="rmact-thumb-none">
                    <FontAwesomeIcon icon={faAward} />
                    No image
                  </span>
                )}
              </div>
              <div className="rmact-body">
                <h3>{cert.ModuleName}</h3>
                <p className="rmact-date">
                  <FontAwesomeIcon icon={faCalendarDays} /> {formatDate(cert.Date) || '—'}
                </p>
                <div className="rmact-files">
                  {cert.Link ? (
                    <a className="rmact-chip" href={pdfUrl(cert.Link)} target="_blank" rel="noopener noreferrer">
                      <FontAwesomeIcon icon={faFilePdf} /> PDF
                    </a>
                  ) : (
                    <span className="rmact-chip is-missing">
                      <FontAwesomeIcon icon={faFilePdf} /> No PDF
                    </span>
                  )}
                  {cert.Img ? (
                    <a className="rmact-chip" href={imgUrl(cert.Img)} target="_blank" rel="noopener noreferrer">
                      <FontAwesomeIcon icon={faImage} /> Image
                    </a>
                  ) : (
                    <span className="rmact-chip is-missing">
                      <FontAwesomeIcon icon={faImage} /> No image
                    </span>
                  )}
                </div>
              </div>
              <div className="rmact-actions">
                <button type="button" onClick={() => handleEditCertificate(cert)}>
                  <FontAwesomeIcon icon={faPen} /> Edit
                </button>
                <button
                  type="button"
                  className="is-danger"
                  onClick={() => {
                    setDeleteError('');
                    setToDelete(cert);
                  }}
                  aria-label={`Delete ${cert.ModuleName} certificate`}
                >
                  <FontAwesomeIcon icon={faTrashCan} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

        </>
      )}

      {/* ---------- Add / edit ---------- */}
      {showModal && (
        <div className="rmact-overlay" onMouseDown={(e) => e.target === e.currentTarget && !saving && setShowModal(false)}>
          <form className="rmact-modal" role="dialog" aria-modal="true" aria-labelledby="rmact-form-title" onSubmit={handleSubmit}>
            <div className="rmact-modal-head">
              <h2 id="rmact-form-title">{isEdit ? 'Edit certificate' : 'Add certificate'}</h2>
              <button type="button" className="rmact-x" onClick={() => setShowModal(false)} disabled={saving} aria-label="Close">
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="rmact-modal-body">
              <div className="rmact-two">
                <label className="rmact-field">
                  <span>Module</span>
                  <select name="ModuleId" value={currentCertificate.ModuleId ?? ''} onChange={handleInputChange} required>
                    <option value="">{availableModules.length ? 'Select module' : 'All modules already have a certificate'}</option>
                    {availableModules.map((m) => (
                      <option key={m.Id} value={m.Id}>
                        {m.ModuleName}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="rmact-field">
                  <span>Issue date</span>
                  <input type="date" name="Date" value={currentCertificate.Date} onChange={handleInputChange} required />
                </label>
              </div>

              {currentCertificate.ModuleId && (
                <div className={`rmact-make ${template?.config ? '' : 'is-off'}`}>
                  {template?.config ? (
                    <>
                      <label className="rmact-field">
                        <span>Name on the certificate</span>
                        <input type="text" value={certName} onChange={(e) => setCertName(e.target.value)} maxLength={80} />
                      </label>
                      <button type="button" className="rmact-btn" onClick={createFromTemplate} disabled={making || busyUploading}>
                        <FontAwesomeIcon icon={faWandMagicSparkles} /> {making ? 'Creating…' : 'Create PDF + image from template'}
                      </button>
                      <small>Uses this module’s template, the name above and the issue date. Check the preview, then add.</small>
                      {makeError && <p className="rmact-error">{makeError}</p>}
                    </>
                  ) : (
                    <small>
                      No template for this module {Number(studentType) === 1 ? '(Little)' : Number(studentType) === 2 ? '(Junior)' : ''} yet, so choose
                      the files below, or{' '}
                      <Link to="/Admin/MyCertificates?filter=templates">{template ? 'place the name and date on its template' : 'add its template'}</Link>.
                    </small>
                  )}
                </div>
              )}

              <FilePick
                label="Certificate PDF"
                icon={faFilePdf}
                name="LinkFile"
                accept=".pdf"
                file={currentCertificate.LinkFile}
                current={currentCertificate.Link}
                upload={uploads.LinkFile}
                onPick={handleFileChange}
                onClear={() => clearFile('LinkFile')}
                onRetry={() => startUpload('LinkFile', currentCertificate.LinkFile)}
              />
              <FilePick
                label="Certificate image"
                icon={faImage}
                name="ImgFile"
                accept="image/*"
                file={currentCertificate.ImgFile}
                current={currentCertificate.Img}
                upload={uploads.ImgFile}
                onPick={handleFileChange}
                onClear={() => clearFile('ImgFile')}
                onRetry={() => startUpload('ImgFile', currentCertificate.ImgFile)}
              />
              {(preview || currentCertificate.Img) && (
                <img
                  className="rmact-preview"
                  src={preview || imgUrl(currentCertificate.Img)}
                  alt="Certificate preview"
                />
              )}

              <label className={`rmact-switch ${currentCertificate.SendEmailCongrats ? 'is-on' : ''}`}>
                <input
                  type="checkbox"
                  checked={currentCertificate.SendEmailCongrats === true}
                  onChange={(e) => setCurrentCertificate((p) => ({ ...p, SendEmailCongrats: e.target.checked }))}
                />
                <i aria-hidden="true" />
                <span>
                  <strong>
                    <FontAwesomeIcon icon={faEnvelope} /> Send congratulations email to parents
                  </strong>
                  <small>Tells the parents their child finished the module.</small>
                </span>
              </label>

              {formError && (
                <p className="rmact-error" role="alert">
                  {formError}
                </p>
              )}
            </div>
            <div className="rmact-modal-foot">
              <button type="button" className="rmact-btn rmact-btn--ghost" onClick={() => setShowModal(false)} disabled={saving}>
                Cancel
              </button>
              <button type="submit" className="rmact-btn" disabled={saving || busyUploading || failedUpload}>
                {saving ? 'Saving…' : busyUploading ? 'Uploading files…' : isEdit ? 'Save changes' : 'Add certificate'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ---------- Delete confirm ---------- */}
      {toDelete && (
        <div className="rmact-overlay" onMouseDown={(e) => e.target === e.currentTarget && !deleteBusy && setToDelete(null)}>
          <div className="rmact-dialog" role="alertdialog" aria-modal="true" aria-labelledby="rmact-del-title">
            <span className="rmact-dialog-icon">
              <FontAwesomeIcon icon={faTrashCan} />
            </span>
            <h2 id="rmact-del-title">Delete this certificate?</h2>
            <p>
              {toDelete.ModuleName} · {formatDate(toDelete.Date)}. This can’t be undone.
            </p>
            {deleteError && (
              <p className="rmact-error" role="alert">
                {deleteError}
              </p>
            )}
            <div className="rmact-dialog-actions">
              <button type="button" className="rmact-btn rmact-btn--ghost" onClick={() => setToDelete(null)} disabled={deleteBusy}>
                Cancel
              </button>
              <button type="button" className="rmact-btn rmact-btn--danger" onClick={confirmDelete} disabled={deleteBusy}>
                {deleteBusy ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="rmact-toast" role="status">
          <FontAwesomeIcon icon={faCircleCheck} /> {toast}
        </div>
      )}
    </div>
  );
};

export default Certificates;